// Pure formatters for the Cycle Setup Screener page — no React, tested in isolation.

// The stored window's edges come from the API as ISO dates (DWLF-113 ③);
// nothing about the window is computed here. The only arithmetic is
// presentation: how far today is from an edge the API gave, in words.
// Three absences are three different facts and are worded apart: the API
// not serving the field at all (`undefined`, an older API), the store holding
// no window for this series (`null`), and an edge the store did not fold ('–').

const DAY_MS = 24 * 60 * 60 * 1000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// The setup's `timing.verification` is a string ('unverified'); the chart's
// window carries it as `{ state, reason }`. Read either, never interpolate an
// object, and never claim a state the API did not send.
export const verificationLabel = (v) => {
  if (typeof v === 'string' && v) return v;
  if (v && typeof v === 'object' && typeof v.state === 'string' && v.state) return v.state;
  return 'unverified';
};

const edgeMs = (edge) => {
  const m = typeof edge?.date === 'string' ? ISO_DATE.exec(edge.date) : null;
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** '6 Jul', or '22 Mar 2027' when the year is not the current one. '–' when the store did not fold the edge. */
export const shortDate = (edge, nowMs) => {
  const t = edgeMs(edge);
  if (t === null) return '–';
  const d = new Date(t);
  const sameYear = d.getUTCFullYear() === new Date(nowMs).getUTCFullYear();
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${sameYear ? '' : ` ${d.getUTCFullYear()}`}`;
};

/** A distance in words: days under two weeks, weeks after that. */
export const spanWords = (ms) => {
  const days = Math.max(0, Math.round(ms / DAY_MS));
  if (days < 14) return `${days} day${days === 1 ? '' : 's'}`;
  const weeks = Math.round(days / 7);
  return `${weeks} weeks`;
};

/** Line one: the stage and how long since the last weekly pivot, e.g. 'LATE · 44 weeks since last low'. */
export const describeWeeklyStage = (stageLabel, daysSincePivot, side) => {
  const pivot = side === 'short' ? 'high' : 'low';
  const stage = typeof stageLabel === 'string' && stageLabel ? stageLabel : 'STAGE ?';
  if (!Number.isFinite(daysSincePivot)) return stage;
  return `${stage} · ${spanWords(daysSincePivot * DAY_MS)} since last ${pivot}`;
};

// A stored edge is the Monday that opens its week; the band's CLOSING edge
// stays open until that week ends — the same boundary the stage uses
// (`closes.endTime`). It sets the DISTANCE only; which word ("ends" or
// "ended") is the API's `phase`.
const WEEK_MS = 7 * DAY_MS;
const closingWeekEnd = (closes) => (closes === null ? null : closes + WEEK_MS);
const startOfUtcDay = (ms) => ms - (ms % DAY_MS);

/**
 * Where today sits against the band. The API's `phase` says before, open,
 * or past the window. Inside 'open' the dates separate the stretch before
 * the blue band (the window's earliest part) from the band itself, and they
 * set every distance. Whole UTC days, so an edge reads as "today", never
 * "0 days"; where the phase and the dates disagree, the phase is stated
 * without a distance.
 */
const whereToday = (timing, nowMs) => {
  const today = startOfUtcDay(nowMs);
  const opens = edgeMs(timing.opens);
  const end = closingWeekEnd(edgeMs(timing.closes));
  // All three are UTC midnights. Equal = the edge is today. The wrong side
  // (the API's phase and the dates disagree) states no distance at all
  // rather than an invented one.
  const until = (edge, verb, bare) => {
    if (edge === null || edge < today) return ` · ${bare}`;
    return edge === today ? ` · ${verb} today` : ` · ${verb} in ${spanWords(edge - today)}`;
  };
  const since = (edge) => {
    if (edge === null || edge > today) return ' · ended';
    return edge === today ? ' · ended today' : ` · ended ${spanWords(today - edge)} ago`;
  };
  switch (timing.phase) {
    case 'upcoming':
      return until(opens, 'opens', 'not open yet');
    case 'open':
      if (opens !== null && today < opens) return until(opens, 'opens', 'not open yet');
      return until(end, 'ends', 'open now');
    case 'overdue':
    case 'missed':
      return since(end);
    default:
      return '';
  }
};

/**
 * Lines two and three: the core window (the blue band, 25th–75th percentile)
 * with where today sits against it, then the hard max and the sample size.
 *   'Window 6 Jul – 31 Aug · ended 3 weeks ago'
 *   'Last chance 30 Nov · from 5 past cycles'
 * `null`/`undefined` timing returns a single line saying which absence it is.
 */
export const describeStoredWindow = (timing, nowMs) => {
  if (timing === undefined) return ['window not served by this API'];
  if (timing === null) return ['no stored window'];
  const band = `Window ${shortDate(timing.opens, nowMs)} – ${shortDate(timing.closes, nowMs)}${whereToday(timing, nowMs)}`;
  const n = Number.isFinite(timing.n) ? ` · from ${timing.n} past cycle${timing.n === 1 ? '' : 's'}` : '';
  const ruler = timing.rulerAgreesWithState === false ? ' · ruler lags' : '';
  // "unverified" is said once for the page (`anyUnverified`); a row states
  // its verification only when it is something else.
  const v = verificationLabel(timing.verification);
  const verification = v === 'unverified' ? '' : ` · ${v}`;
  return [band, `Last chance ${shortDate(timing.hardMax, nowMs)}${n}${verification}${ruler}`];
};

/**
 * True when any row's window is unverified — the page then says so once,
 * because each such row leaves the label out. A row with no stored window
 * has no verification to state and does not count either way.
 */
export const anyUnverified = (rows) => rows
  .some((s) => s?.weekly?.timing && verificationLabel(s.weekly.timing.verification) === 'unverified');
